// Add a person by hand (AdminPeople.dc.html), owner and admin: their name, the email they log in with,
// a reason, what to give them and how it was paid for (Adam, 4 Oct, answer 2: nothing picked until
// Adam picks). add-person makes their people row and the grant together, and emails them how to log
// in at the product's door (C36, S08). Someone already in the hub comes back with their id, so the
// panel offers their page instead.
import { useMemo, useState } from 'react'
import { Button, ButtonLink, Chip, Chips, Options, GRANT_SOURCES, Select, TextInput, useAction, useAdmin, adminPath, todayInUk } from '../ui/index.js'
import { ADD, byBoardOrder, shortName } from './words.js'
import { cohortsOf, defaultCohort, grantable } from './data.js'

export function AddPerson({ products, cohorts, onAdded }) {
  const { api, setFlash } = useAdmin()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [reason, setReason] = useState('')
  const [productId, setProductId] = useState('')
  const [cohortId, setCohortId] = useState('')
  const [source, setSource] = useState(null)
  const save = useAction(body => api.add_person(body))

  const options = useMemo(() => byBoardOrder(products.filter(grantable)), [products])
  const product = options.find(p => p.id === productId) || null
  const productCohorts = product ? cohortsOf(cohorts, product.id) : []
  const filled = name.trim() !== '' && email.indexOf('@') > 0 && reason.trim() !== ''
  const ready = filled && product && source
  const hint = !filled ? ADD.needs : !product ? ADD.needsProduct : !source ? ADD.needsSource : ADD.ready

  function pick(p) {
    setProductId(p.id)
    setCohortId(defaultCohort(p, cohorts, todayInUk()))
    save.clear()
  }

  async function submit(e) {
    e.preventDefault()
    if (!ready) return
    const body = {
      email: email.trim(), full_name: name.trim(), product_id: product.id, source, reason: reason.trim(), email_them: true,
    }
    if (cohortId) body.cohort_id = cohortId
    const answer = await save.run(body)
    if (!answer) return
    setFlash({
      text: answer.message,
      actions: answer.person_id ? <ButtonLink size="sm" to={adminPath.person(answer.person_id)}>{ADD.openNew(name.trim())}</ButtonLink> : null,
    })
    setName(''); setEmail(''); setReason(''); setProductId(''); setCohortId(''); setSource(null)
    onAdded?.(answer)
  }

  const existing = save.error?.code === 'person_exists' ? save.error.body?.person_id : null
  return (
    <form className="apl-panel" onSubmit={submit} aria-labelledby="apl-add-h" noValidate>
      <h2 className="apl-panel-title" id="apl-add-h">{ADD.title}</h2>
      <div className="apl-grid3">
        <TextInput aria-label={ADD.name} placeholder={ADD.name} value={name} onChange={setName} autoComplete="off" maxLength={120} />
        <TextInput aria-label="Email" type="email" placeholder={ADD.email} value={email} onChange={setEmail} autoComplete="off" />
        <TextInput aria-label="Reason" placeholder={ADD.reason} value={reason} onChange={setReason} autoComplete="off" maxLength={1000} />
      </div>
      <Chips label={ADD.give}>
        {options.map(p => (
          <Chip key={p.id} product={p} pressed={p.id === productId} onClick={() => pick(p)}>{shortName(p)}</Chip>
        ))}
      </Chips>
      {productCohorts.length > 0 && (
        <label className="apl-inline-field">
          <span className="apl-inline-label">{ADD.cohort}</span>
          <Select value={cohortId} onChange={setCohortId} placeholder={ADD.noCohort}
            options={productCohorts.map(c => ({ value: c.id, label: c.name }))} />
        </label>
      )}
      <div className="apl-source">
        <span className="apl-step-label" id="apl-add-source">{ADD.source}</span>
        <Options labelledBy="apl-add-source" row className="apl-compact" value={source} onChange={setSource} options={GRANT_SOURCES} />
      </div>
      <div className="apl-actions">
        <Button kind="ink" type="submit" disabled={!ready} busy={save.busy} busyLabel={ADD.busy}>{ADD.button}</Button>
        <span className="apl-hint" aria-live="polite">{hint}</span>
      </div>
      {save.error && (
        <p className="ad-error" role="alert">
          {save.error.message}
          {existing && <> <ButtonLink size="xs" to={adminPath.person(existing)}>{ADD.openExisting(email.trim())}</ButtonLink></>}
        </p>
      )}
    </form>
  )
}
